import { CloseOutlined, HomeOutlined, ShrinkOutlined } from "@ant-design/icons";
import { Button, Col, Row, Switch } from "antd";
import Cookies from "js-cookie";
import { useRef, useState } from "react";
import Draggable from "react-draggable";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa6";

import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { RecordStoreModal } from "../../components/modal/RecordStoreModal";
import { FILE_STATUS } from "../../config/Constant";
import { useSideBarStore } from "../../store/SideBarStore";
import { useInfoUser } from "../../store/UserStore";
import { formatTimeBySeconds } from "../../utils/formatTime";
import "./style.scss";

const MAX_DURATION = 15 * 60;

const SmallRecord = () => {
  const navigate = useNavigate();
  const { languageMap, user } = useInfoUser();
  const { switchIsRecord } = useSideBarStore((state) => state);
  const [setting, setSetting] = useState({
    screen: "FULL_SCREEN",
    // camera: false,
    // audio: false,
    micro: false,
  });

  const [status, setStatus] = useState("idle");
  const [isRecording, setIsRecording] = useState(false);
  const [isCollapse, setIsCollapse] = useState(false);
  const chunkIndexRef = useRef(0);
  const chunks = useRef([]);
  const [payload, setPayload] = useState({
    conversationId: user?.selfConversationId,
    folder: "CONVERSATION",
    name: uuidv4(),
    token: Cookies.get("access_token"),
    status: FILE_STATUS.READY,
  });
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const [isStoreConfirm, setIsStoreConfirm] = useState(false);

  //time
  const timerRef = useRef(null);
  const [duration, setDuration] = useState(0);

  const start = async () => {
    try {
      setIsRecording(true);
      setDuration(0);

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: false,
      });

      let finalStream;
      let mic;

      if (setting.micro) {
        try {
          mic = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          const audioContext = new AudioContext();
          const destination = audioContext.createMediaStreamDestination();

          const micSource = audioContext.createMediaStreamSource(mic);
          micSource.connect(destination);

          finalStream = new MediaStream([
            ...displayStream.getVideoTracks(),
            ...destination.stream.getAudioTracks(),
          ]);
        } catch (e) {
          setSetting((prev) => ({ ...prev, micro: false }));
          finalStream = new MediaStream([...displayStream.getVideoTracks()]);
        }
      } else {
        finalStream = new MediaStream([...displayStream.getVideoTracks()]);
      }

      const recorder = new MediaRecorder(finalStream, {
        mimeType: "video/webm;codecs=vp9,opus",
        videoBitsPerSecond: 1_500_000,
        audioBitsPerSecond: 64_000,
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks?.current?.push(e.data);
        }
      };

      recorder.onstop = async () => {
        displayStream.getTracks().forEach((t) => t.stop());
        mic?.getTracks().forEach((t) => t.stop());
        stop();
      };

      displayStream.getVideoTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          if (recorder.state !== "inactive") {
            recorder.stop();
          }
        });
      });

      recorder.start(1000);
      recorderRef.current = recorder;
      streamRef.current = finalStream;
      setStatus("recording");
      startTimer();
    } catch (err) {
      stopTimer();
      setIsCollapse(false);
      setIsRecording(false);
      console.error("Failed to start recording:", err);
    }
  };

  const pause = () => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.pause();
      setStatus("paused");
    }
  };

  const resume = () => {
    if (recorderRef.current?.state === "paused") {
      recorderRef.current.resume();
      setStatus("recording");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }

    setStatus("stopped");
    stopTimer();
    setIsCollapse(false);
    setIsRecording(false);
    setIsStoreConfirm(true);
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setDuration((prev) => {
        if (prev + 1 >= MAX_DURATION) {
          stop();
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const closeRecording = () => {
    if (!isRecording) switchIsRecord(false);
  };

  return (
    <Draggable bounds="parent" cancel="input,textarea,button,select">
      <div className="absolute top-[100px] right-[50px] bg-[#2a56b9] text-white rounded-[10px] cursor-grabbing z-[1000] shadow-lg">
        <Row className="pt-[10px] w-[100%]">
          <Col span={3} />
          <Col span={18} className="text-[16px] font-semibold text-center">
            {languageMap?.["record.screen"] ?? "Record (15 minutes)"}
          </Col>
        </Row>
        <div className="w-[230px] pb-[10px] px-[20px]">
          <div
            className={`mt-[20px] flex flex-row gap-[20px] ${isRecording ? "justify-end" : "justify-between"}`}
          >
            {!isRecording ? (
              <button onClick={closeRecording}>
                <CloseOutlined className="text-[20px]" />
              </button>
            ) : (
              <button onClick={() => setIsCollapse((prev) => !prev)}>
                <ShrinkOutlined className="text-[20px]" />
              </button>
            )}
            {!isRecording && (
              <button
                onClick={() =>
                  navigate(`/conversation/chat/${user?.selfConversationId}`)
                }
              >
                <HomeOutlined className="text-[20px]" />
              </button>
            )}
          </div>
          {!isCollapse && (
            <div>
              <div className="mt-[20px] flex items-center gap-[10px]">
                {setting?.micro ? (
                  <FaMicrophone size={23} />
                ) : (
                  <FaMicrophoneSlash size={23} />
                )}
                <Switch
                  disabled={isRecording}
                  value={setting?.micro}
                  onChange={(value) => setSetting({ ...setting, micro: value })}
                />
              </div>
            </div>
          )}
          <div className="mt-[20px] flex items-center justify-center">
            {isRecording ? (
              <Button type="primary" className="bg-[#ff623e]" onClick={stop}>
                <div>{formatTimeBySeconds(duration)} Stop recording</div>
              </Button>
            ) : (
              <Button type="primary" className="bg-[#3cb371]" onClick={start}>
                Start recording
              </Button>
            )}
          </div>
        </div>
        {isStoreConfirm && (
          <RecordStoreModal
            title="Do you want to save this record ?"
            duration={duration}
            payload={payload}
            changeName={(e) =>
              setPayload({ ...payload, name: e?.target?.value })
            }
            onCancel={() => {
              setIsStoreConfirm(false);
              chunks.current = [];
              setPayload({
                ...payload,
                name: uuidv4(),
              });
            }}
            open={isStoreConfirm}
            uploadFile={new Blob(chunks?.current, { type: "video/webm" })}
            totalChunk={chunkIndexRef.current}
          />
        )}
      </div>
    </Draggable>
  );
};

export { SmallRecord };
